import { useState } from "react"
import { AlertCircle, Check, Tag, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

// The Pay now window's discount code box: the after-posting twin of Post a
// Project's code step, with the same words. The server decides whether a code
// may still change (`updatable`: every order in the project unpaid); one code
// per project, so applying a new one replaces the old. The parent keys the box
// by the applied code, so the field clears once a code lands.
function discountLabel(discount) {
  const value = Number(discount.value)
  if (!value) return ""
  return discount.percentage ? ` (${value}% off)` : ` ($${value} off)`
}

export default function DiscountCodeBox({ discount, updatable, busy, error, onApply, onRemove }) {
  const [code, setCode] = useState("")

  const apply = () => {
    if (!code.trim()) return
    onApply(code.trim())
  }

  return (
    <div className="flex flex-col gap-[10px] rounded-[11px] border border-border bg-accent px-[15px] py-[14px]">
      <div className="flex items-center gap-[7px] text-[12px] font-bold text-foreground">
        <Tag className="size-[15px] text-primary" strokeWidth={2} />
        Discount code
      </div>

      {!updatable ? (
        <p className="text-[12px] leading-snug text-muted-foreground">
          Discount codes can not be applied to this order because an order in this project has already been paid for.
        </p>
      ) : (
        <>
          {discount ? (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-done/40 bg-done/10 px-3.5 py-2.5">
              <span className="flex items-center gap-2 text-[12.5px] font-semibold text-foreground">
                <Check className="size-3.5 text-done" strokeWidth={2.6} />
                {discount.code} applied
                {discountLabel(discount)}
              </span>
              <button
                type="button"
                onClick={onRemove}
                disabled={busy}
                className="flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
                aria-label="Remove discount"
              >
                <X className="size-3.5" strokeWidth={2} />
              </button>
            </div>
          ) : (
            <p className="text-[12px] leading-snug text-muted-foreground">
              Discount codes can not be used in conjunction with other discounts or special rates.
            </p>
          )}
          {/* Stays open while a code is applied: a new code replaces the old in
              one step, and a refused one leaves the old code in place. */}
          <div className="flex items-center gap-2.5">
            <Input
              value={code}
              placeholder="Enter a code"
              aria-label="Discount code"
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && apply()}
              className="max-w-[220px] bg-card"
            />
            <Button variant="outline" onClick={apply} disabled={busy || !code.trim()} className="font-semibold">
              {busy ? "Applying…" : "Apply"}
            </Button>
          </div>
        </>
      )}

      {error && (
        <p className="flex items-center gap-1.5 text-[12px] font-semibold text-destructive">
          <AlertCircle className="size-3.5" strokeWidth={2.2} />
          {error}
        </p>
      )}
    </div>
  )
}
